// components/StatsHeader/StatsHeader.jsx
import { useMemo, useState, useEffect } from "react";
import "./StatsHeader.css";
import { useLanguage } from "../../../../LanguageProvider";
import { IoCloseOutline } from "react-icons/io5";
import { TbTicket, TbCoins, TbArrowsLeftRight, TbWallet, TbCalendarEvent, TbChartLine, TbPercentage, TbX } from "react-icons/tb";
import { BiTrendingUp, BiTrendingDown } from "react-icons/bi";

export default function StatsHeader({
  tickets = [],
  event = {},
  currency = "TRY",
  onTypeFilterChange,
  activeType: controlledActiveType,
  __cancelledKey = "__CANCELLED__",
}) {
  const { getTranslation } = useLanguage();
  const [internalActiveType, setInternalActiveType] = useState(null);
  const [selectedStats, setSelectedStats] = useState(["totalSold", "totalRevenue", "participationRate", "dailySales"]);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const activeType = controlledActiveType !== undefined ? controlledActiveType : internalActiveType;

  useEffect(() => {
    if (!activeType) return;
    const hasType = tickets.some((t) => (t.type || t.title) === activeType);
    const hasCancelled = activeType === __cancelledKey && tickets.some((t) => t.status === "0");
    if (!hasType && !hasCancelled) {
      if (controlledActiveType === undefined) setInternalActiveType(null);
      onTypeFilterChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  const toNumber = (v) => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === "string" ? v.replace(",", ".") : v;
    const parsed = parseFloat(n);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const fmtMoney = (n) =>
    new Intl.NumberFormat("tr-TR", { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0);

  const sameDayTR = (unixA, unixB) => {
    const a = new Date(parseInt(unixA, 10) * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
    const b = new Date(parseInt(unixB, 10) * 1000).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
    return a === b;
  };

  const todayKey = new Date().toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });
  
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = yesterday.toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" });

  const stats = useMemo(() => {
    if (!tickets?.length) {
      return {
        totalSold: 0,
        soldToday: 0,
        soldYesterday: 0,
        byType: {},
        totalRevenue: 0,
        refundedRevenue: 0,
        netBalance: 0,
        usedOnEventDay: 0,
        totalUsed: 0,
        cancelledCount: 0,
        refundedCount: 0,
      };
    }

    const refundedCount = tickets.filter((t) => t.isRefund === "1").length;

    const isSold = (t) => t.status === "1" && t.isRefund !== "1";
    const sold = tickets.filter(isSold);

    const soldToday = sold.filter((t) => {
      if (!t.paymentDateTime) return false;
      const key = new Date(parseInt(t.paymentDateTime, 10) * 1000).toLocaleDateString("tr-TR", {
        timeZone: "Europe/Istanbul",
      });
      return key === todayKey;
    }).length;

    const soldYesterday = sold.filter((t) => {
      if (!t.paymentDateTime) return false;
      const key = new Date(parseInt(t.paymentDateTime, 10) * 1000).toLocaleDateString("tr-TR", {
        timeZone: "Europe/Istanbul",
      });
      return key === yesterdayKey;
    }).length;

    const byType = sold.reduce((acc, t) => {
      const key = t.type || t.title || getTranslation("other");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const totalRevenue = tickets.reduce((sum, t) => {
      const amount = toNumber(t.totalPaid ?? t.paid);
      return sum + (t.isRefund === "1" ? 0 : amount);
    }, 0);

    const refundedRevenue = tickets.reduce((sum, t) => {
      const amount = toNumber(t.totalPaid ?? t.paid);
      return sum + (t.isRefund === "1" ? amount : 0);
    }, 0);

    const netBalance = totalRevenue - refundedRevenue;
    const totalUsed = tickets.filter((t) => t.isUsed === "1").length;

    let usedOnEventDay = 0;
    const evStart = event?.eventStartDateTime ? parseInt(event.eventStartDateTime, 10) : null;
    const evEnd = event?.eventEndDateTime ? parseInt(event.eventEndDateTime, 10) : null;

    if (evStart) {
      usedOnEventDay = tickets.filter((t) => {
        if (t.isUsed !== "1" || !t.usedDateTime) return false;
        const used = parseInt(t.usedDateTime, 10);
        const sameDay = sameDayTR(used, evStart);
        const inRange = evEnd ? used >= evStart && used <= evEnd : sameDay;
        return sameDay || inRange;
      }).length;
    }

    const cancelledCount = tickets.filter((t) => t.status === "0").length;

    return {
      totalSold: sold.length,
      soldToday,
      soldYesterday,
      byType,
      totalRevenue,
      refundedRevenue,
      netBalance,
      usedOnEventDay,
      totalUsed,
      cancelledCount,
      refundedCount
    };
  }, [tickets, todayKey, yesterdayKey, event?.eventStartDateTime, event?.eventEndDateTime, getTranslation]);

  const participationRate = stats.totalSold > 0 
    ? Math.round((stats.totalUsed / stats.totalSold) * 100) 
    : 0;

  const dailySalesChange = stats.soldYesterday > 0
    ? Math.round(((stats.soldToday - stats.soldYesterday) / stats.soldYesterday) * 100)
    : stats.soldToday > 0 ? 100 : 0;

  const formatTodayDate = () => {
    const today = new Date();
    return today.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "Europe/Istanbul"
    });
  };

  const allStatsOptions = [
    { value: "totalSold", label: getTranslation("totalSoldTickets") },
    { value: "totalRevenue", label: getTranslation("totalRevenue") },
    { value: "refunds", label: getTranslation("refunds") },
    { value: "balance", label: getTranslation("balance") },
    { value: "usedOnEventDay", label: getTranslation("usedOnEventDay") },
    { value: "participationRate", label: getTranslation("participationRate") },
    { value: "dailySales", label: getTranslation("dailySales") },
    { value: "cancelled", label: getTranslation("cancelled") },
  ];

  const handleStatsChange = (value) => {
    if (selectedStats.includes(value)) {
      setSelectedStats(selectedStats.filter(s => s !== value));
    } else {
      setSelectedStats([...selectedStats, value]);
    }
  };

  const renderStatCard = (key) => {
    switch (key) {
      case "totalSold":
        return (
          <div key={key} className="stat-card">
            <TbTicket className="stat-card-icon" />
            <span className="stat-label">{getTranslation("totalSoldTickets")}</span>
            <span className="stat-value">{stats.totalSold}</span>
            {stats.cancelledCount > 0 && (
              <span className="stat-sub">
                {stats.cancelledCount} {getTranslation("cancelled").toLowerCase()} ile {stats.totalSold} {getTranslation("totalSold").toLowerCase()}
              </span>
            )}
          </div>
        );
      case "totalRevenue":
        return (
          <div key={key} className="stat-card">
            <TbCoins className="stat-card-icon" />
            <span className="stat-label">{getTranslation("totalRevenue")}</span>
            <span className="stat-value">{fmtMoney(stats.totalRevenue)}</span>
            <span className="stat-sub">{getTranslation("grossExcludingRefunds")}</span>
          </div>
        );
      case "refunds":
        return (
          <div key={key} className="stat-card">
            <TbArrowsLeftRight className="stat-card-icon" />
            <span className="stat-label">
              {getTranslation("refunds")}{" "}
              {stats.refundedRevenue > 0 && `(${stats.refundedCount})`}
            </span>
            <span className="stat-value">{fmtMoney(stats.refundedRevenue)}</span>
          </div>
        );
      case "balance":
        return (
          <div key={key} className="stat-card">
            <TbWallet className="stat-card-icon" />
            <span className="stat-label">{getTranslation("balance")}</span>
            <span className="stat-value">{fmtMoney(stats.netBalance)}</span>
            <span className="stat-sub">{getTranslation("afterRefunds") || "İadeler sonrası"}</span>
          </div>
        );
      case "usedOnEventDay":
        return (
          <div key={key} className="stat-card">
            <TbCalendarEvent className="stat-card-icon" />
            <span className="stat-label">{getTranslation("usedOnEventDay")}</span>
            <span className="stat-value">{stats.usedOnEventDay}</span>
          </div>
        );
      case "participationRate":
        return (
          <div key={key} className="stat-card">
            <TbPercentage className="stat-card-icon" />
            <span className="stat-label">{getTranslation("participationRate")}</span>
            <span className="stat-value">{participationRate}%</span>
          </div>
        );
      case "dailySales":
        return (
          <div key={key} className="stat-card">
            <TbChartLine className="stat-card-icon" />
            <span className="stat-label">{getTranslation("dailySales")}</span>
            <span className="stat-value">{stats.soldToday}</span>
            <span className="stat-sub">
              {formatTodayDate()}
              {stats.soldYesterday > 0 && (
                <span className={`stat-change ${dailySalesChange >= 0 ? "stat-change--positive" : "stat-change--negative"}`}>
                  {dailySalesChange >= 0 ? <BiTrendingUp /> : <BiTrendingDown />}
                  {Math.abs(dailySalesChange)}%
                </span>
              )}
            </span>
          </div>
        );
      case "cancelled":
        return (
          <div key={key} className="stat-card stat-card--danger">
            <TbX className="stat-card-icon" />
            <span className="stat-label">{getTranslation("cancelled")}</span>
            <span className="stat-value">{stats.cancelledCount}</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="stats-header">
      <div className="stats-filter-wrapper">
        <button
          className="stats-filter-button"
          onClick={() => setIsStatsModalOpen(true)}
        >
          {getTranslation("selectStatsToShow")}
        </button>
      </div>

      {isStatsModalOpen && (
        <div className="stats-modal-overlay" onClick={() => setIsStatsModalOpen(false)}>
          <div className="stats-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="stats-modal-header">
              <h3>{getTranslation("selectStatsToShow")}</h3>
              <button
                className="stats-modal-close"
                onClick={() => setIsStatsModalOpen(false)}
              >
                <IoCloseOutline size={20} />
              </button>
            </div>
            <div className="stats-modal-body">
              <div className="stats-filter-checkboxes">
                {allStatsOptions.map(opt => (
                  <label key={opt.value} className="stats-filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedStats.includes(opt.value)}
                      onChange={() => handleStatsChange(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="stats-grid">
        {selectedStats.length > 0 ? (
          selectedStats.map(key => renderStatCard(key))
        ) : (
          allStatsOptions.map(opt => renderStatCard(opt.value))
        )}
      </div>
    </div>
  );
}
